import { Http } from '@mygpt/app/http';

// 集成平台类型
export type IntegrationPlatform = 'line' | 'wowtalk' | 'lineworks' | 'twilio' | 'lark' | 'teams' | 'zendesk';

// Line 集成配置
export interface LineIntegrationConfig {
  channel_id: string;
  channel_secret: string;
  channel_access_token: string;
}

// WowTalk 集成配置
export interface WowTalkIntegrationConfig {
  company_id: string;
  from_account: string;
  from_password: string;
  api_base_url: string;
  api_key: string;
  callback_token?: string;
}

// LINE WORKS 集成配置
export interface LineWorksIntegrationConfig {
  client_id: string;
  client_secret: string;
  service_account: string;
  bot_id: string;
  private_key: string;
}

// Twilio 集成配置（电话受付，只存秘钥）
// Lark 集成配置：通知 webhook 与日历应用凭据各自可选、至少其一
export interface LarkIntegrationConfig {
  webhook_url?: string;
  secret?: string;
  app_id?: string;
  app_secret?: string;
  friendly_name?: string;
}

export interface TwilioIntegrationConfig {
  account_sid: string;
  auth_token: string;
  phone_number: string;
  friendly_name?: string;
}

// Microsoft Teams 集成配置（一对一：一个 Teams bot 对应一个 robot）
export interface TeamsIntegrationConfig {
  app_id: string;
  app_password: string;
  tenant_id?: string;
  app_type?: string;
  display_name?: string;
  accent_color?: string;
  friendly_name?: string;
}

// 集成配置项
export interface IntegrationItem {
  id: string;
  platform: IntegrationPlatform;
  // LINE 字段
  channel_id?: string;
  channel_secret?: string;
  channel_access_token?: string;
  webhook_url?: string;
  // WowTalk 字段
  company_id?: string;
  from_account?: string;
  from_password?: string;
  api_base_url?: string;
  api_key?: string;
  callback_token?: string;
  callback_url?: string;
  // LINE WORKS 字段
  client_id?: string;
  client_secret?: string;
  service_account?: string;
  bot_id?: string;
  private_key?: string;
  // Twilio 字段（电话受付）
  account_sid?: string;
  auth_token?: string;
  phone_number?: string;
  friendly_name?: string;
  status?: string;
  last_tested_at?: string;
  // Lark 字段（通知 webhook + 日历应用凭据，均掩码）
  secret?: string;
  app_id?: string;
  app_secret?: string;
  // Teams 字段（app_id 复用上面的掩码字段）
  app_password?: string;
  tenant_id?: string;
  app_type?: string;
  display_name?: string;
  accent_color?: string;
  package_url?: string;
  // Zendesk 字段（api_token 掩码；webhook_url / friendly_name / status / last_tested_at 复用上面）
  connection_id?: string;
  subdomain?: string;
  admin_email?: string;
  api_token?: string;
  connection_status?: string;
  authentication?: import('./zendesk').ZendeskAuthentication;
  webhook_id?: string | null;
  webhook_registered?: boolean;
  target_form_ids?: number[];
  signature_patterns?: string[];
  draft_note_enabled?: boolean;
  show_sources_in_note?: boolean;
  sink_dataset_id?: string | null;
  last_error?: string | null;
  // 同じ接続を使う他 bot と対象フォームの重なり（型は zendesk.ts の ZendeskConnectionSharer と同じ）
  shared_with?: {
    robot_id: string;
    robot_name?: string | null;
    target_form_ids: (number | string)[];
    form_overlap: boolean;
  }[];
  form_overlap?: boolean;
  // 「現在の有効設定」（S02）：この bot の Zendesk ドラフトが実際にどう動くか
  effective?: {
    model: { value?: string | null; source: 'bot' | 'global_default' };
    robot_type?: string | null;
    datasets: { id: string; name: string }[];
    no_answer_behavior: { require_grounding: boolean; gates: string[]; source: string };
    target_forms: { value: (number | string)[]; all_forms: boolean; source: string };
    note_target: { value: 'internal_note' | 'dry_run'; source: string };
    review_policy: { dataset_id?: string | null; publish_mode: string; source: string };
    learning?: { enabled: boolean; dataset_id?: string | null; source: string };
  };
  // 健全性を 4 つに分けて見せる（S01）：接続 / Webhook / 受信 / 投稿
  health?: {
    connection: { status: string; last_tested_at?: string | null; last_error?: string | null };
    webhook: { registered: boolean; id?: string | null; last_error?: string | null };
    receive: { last_event_at?: string | null; last_error?: string | null };
    post: { last_posted_at?: string | null; last_error?: string | null; dry_run: boolean };
  };
  // 1 つの接続を複数の用途で使う：回答ドラフト / 修正の学習 / ナレッジ取込
  purposes?: {
    draft: { enabled: boolean; paused: boolean; paused_at?: string | null; dry_run: boolean };
    learn_edits?: { enabled: boolean; dataset_id?: string | null; source: string } | null;
    import: { dataset_id: string; dataset_name?: string | null; status: string; last_run_at?: string | null }[];
  };
  dependencies?: { bots: number; datasets: number };
  paused_at?: string | null;
  // 通用字段
  robot_id: string;
  created_at: string;
  updated_at: string;
}

// 获取集成列表的响应
export interface GetIntegrationsResponse {
  integrations: IntegrationItem[];
}

// 创建集成的响应
export interface CreateIntegrationResponse {
  id: string;
}

/**
 * 获取指定机器人的所有外部平台接入配置列表
 * @param robotId 机器人ID
 * @returns 集成配置列表
 */
export const getIntegrations = (robotId: string) => {
  const { get } = Http.use();
  return get<GetIntegrationsResponse>(`/robots/${robotId}/integrations`);
};

/**
 * 创建 Line 平台的集成配置
 * @param robotId 机器人ID
 * @param config Line 配置信息
 * @returns 创建的集成配置ID
 */
export const createLineIntegration = (robotId: string, config: LineIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/line`, config);
};

/**
 * 删除指定的 Line 集成配置
 * @param robotId 机器人ID
 * @param configId 配置ID
 * @returns 删除操作结果
 */
export const deleteLineIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/line/${configId}`);
};

/**
 * 创建 WowTalk 平台的集成配置
 * @param robotId 机器人ID
 * @param config WowTalk 配置信息
 * @returns 创建的集成配置ID
 */
export const createWowTalkIntegration = (robotId: string, config: WowTalkIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/wowtalk`, config);
};

/**
 * 删除指定的 WowTalk 集成配置
 * @param robotId 机器人ID
 * @param configId 配置ID
 * @returns 删除操作结果
 */
export const deleteWowTalkIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/wowtalk/${configId}`);
};

/**
 * 创建 LINE WORKS 平台的集成配置
 * @param robotId 机器人ID
 * @param config LINE WORKS 配置信息
 * @returns 创建的集成配置ID
 */
export const createLineWorksIntegration = (robotId: string, config: LineWorksIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/lineworks`, config);
};

/**
 * 删除指定的 LINE WORKS 集成配置
 * @param robotId 机器人ID
 * @param configId 配置ID
 * @returns 删除操作结果
 */
export const deleteLineWorksIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/lineworks/${configId}`);
};

/**
 * 创建 Twilio 平台的集成配置（电话受付，只存秘钥）
 * 创建时后端会调 Twilio API 校验凭据，无效返回 400
 * @param robotId 机器人ID
 * @param config Twilio 配置信息
 * @returns 创建的集成配置ID + webhook_url
 */
export const createTwilioIntegration = (robotId: string, config: TwilioIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/twilio`, config);
};

/**
 * 删除指定的 Twilio 集成配置
 * @param robotId 机器人ID
 * @param configId 配置ID
 * @returns 删除操作结果
 */
export const deleteTwilioIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/twilio/${configId}`);
};

/**
 * 创建 Lark 通知集成配置（bot 级群机器人 webhook）
 */
export const createLarkIntegration = (robotId: string, config: LarkIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/lark`, config);
};

/**
 * 合并更新 Lark 集成配置：只覆盖非空字段，留空的保持原值
 * （webhook 与日历应用是两组独立凭据，编辑一组不会清掉另一组）
 */
export const updateLarkIntegration = (robotId: string, configId: string, config: LarkIntegrationConfig) => {
  const { put } = Http.use();
  return put<CreateIntegrationResponse>(`/robots/${robotId}/integrations/lark/${configId}`, config);
};

/**
 * 删除指定的 Lark 通知集成配置
 */
export const deleteLarkIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/lark/${configId}`);
};

/**
 * 创建 Microsoft Teams 平台的集成配置（一对一）
 * @param robotId 机器人ID
 * @param config Teams 配置信息（app_id / app_password / tenant_id 等）
 * @returns 创建的集成配置ID
 */
export const createTeamsIntegration = (robotId: string, config: TeamsIntegrationConfig) => {
  const { post } = Http.use();
  return post<CreateIntegrationResponse>(`/robots/${robotId}/integrations/teams`, config);
};

/**
 * 删除指定的 Microsoft Teams 集成配置
 * @param robotId 机器人ID
 * @param configId 配置ID
 */
export const deleteTeamsIntegration = (robotId: string, configId: string) => {
  const { delete: deleteHttp } = Http.use();
  return deleteHttp(`/robots/${robotId}/integrations/teams/${configId}`);
};

/**
 * 下载 Microsoft Teams 安装包 ZIP（manifest.json + 图标）
 * @param robotId 机器人ID
 * @param configId 配置ID
 * @param filename 下载文件名
 */
export const downloadTeamsPackage = async (robotId: string, configId: string, filename = 'GBase-Teams-App.zip') => {
  const http = Http.use();
  const response = await http.get(`/robots/${robotId}/integrations/teams/${configId}/package`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'application/zip' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 100);
  return { success: true };
};
